import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Row as ReactTableRow,
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { cn } from '@Utils';

import { sendErrorEvent } from '@Analytics';
import { bopsApi } from '@Network';
import { RootState } from '@Store';
import ColumnPicker from '../ColumnPicker/columnPicker';
import IndeterminateCheckbox from '../IndeterminateCheckbox/indeterminateCheckbox';

export interface Column {
  Header?: string;
  accessor: string;
  Cell?: ({ row }) => void;
  width?: string;
}

export interface Row {
  values: object;
  depth: number;
  canExpand?: boolean;
  isExpanded?: boolean;
}

export interface TableFilter {
  column: string;
  value?: any;
}

type ColumnSort = {
  id: string;
  desc: boolean;
};
type SortingState = ColumnSort[];

interface TableProps {
  columns: Column[];
  dataResult: object[];
  pagination?: boolean;
  sortable?: boolean;
  footer?: boolean;
  columnPicker?: boolean;
  selectable?: boolean;
  filters?: string;
  trackFilterOptions?: boolean;
  startPagination: number;
  pageSize: number;
  total: number;
  endpoint: string;
  incrementName: string;
  revision: object;
  indexPagination: string;
  disableSortFields?: string[];
  setTotal: (total: number) => void;
  onResponse?: (response: any) => void;

  getFilteredRows?(rows: ReactTableRow<object>[]);
}

const TableServer = (props: TableProps) => {
  const {
    columns,
    pagination,
    sortable,
    footer,
    columnPicker,
    dataResult,
    total,
    selectable,
    filters,
    trackFilterOptions,
    getFilteredRows,
    endpoint,
    startPagination,
    indexPagination,
    pageSize,
    incrementName,
    revision,
    disableSortFields,
    setTotal,
    onResponse,
  } = props;

  const [data, setData] = useState(dataResult);
  const [totalPage, setTotalPage] = useState(total);
  const [currentPage, setCurrentPage] = useState(startPagination);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const incrementEvent = revision[incrementName] ?? 0;

  const [sortBy, setSortBy] = useState<SortingState>([]);

  // Track previous filters to determine when to request filter_options
  const prevFiltersRef = useRef<string | null>(null);

  useEffect(() => {
    if (filters && currentPage !== startPagination) setCurrentPage(startPagination);
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const field = sortBy[0]?.id;
        const order = sortBy[0]?.desc;

        // Determine if we need to fetch filter options (only when trackFilterOptions is enabled)
        const normalizedFilters = filters ?? '';
        let queryToUse = normalizedFilters;
        if (trackFilterOptions) {
          const filtersChanged = prevFiltersRef.current !== normalizedFilters;
          if (filtersChanged) {
            queryToUse = normalizedFilters
              ? `${normalizedFilters}&include_filter_options=true`
              : 'include_filter_options=true';
          }
        }

        const response = await bopsApi[endpoint](queryToUse, currentPage, pageSize, field, order);

        // Update ref AFTER successful fetch
        if (trackFilterOptions) {
          prevFiltersRef.current = normalizedFilters;
        }

        setData(response[indexPagination]);
        setLoading(false);
        setTotalPage(response.total);
        setTotal(response.total);
        if (onResponse) {
          onResponse(response);
        }
      } catch (error) {
        sendErrorEvent(error);
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, filters, incrementEvent, sortBy]);

  const pageCount = totalPage ? Math.ceil(totalPage / pageSize) : 1;
  const {
    footerGroups,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    allColumns,
    getToggleHideAllColumnsProps,
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      manualSortBy: true,
      state: { sortBy },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (selectable) {
        hooks.visibleColumns.push((visibleColumn) => [
          {
            id: 'selection',
            Header: ({ getToggleAllPageRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
            ),
            Cell: ({ row }) => <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />,
          },
          ...visibleColumn,
        ]);
      }
    },
  );

  if (getFilteredRows) {
    getFilteredRows(rows);
  }

  const visibleRows = pagination ? page : rows;

  return (
    <div className="w-full overflow-x-auto">
      {columnPicker && (
        <div className="flex justify-end">
          <ColumnPicker allColumns={allColumns} getToggleHideAllColumnsProps={getToggleHideAllColumnsProps} />
        </div>
      )}
      <table className={cn('w-full border-collapse', loading && 'animate-pulse')} {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
              {headerGroup.headers.map((column) => {
                const isSortDisabled = disableSortFields?.includes(column.id);
                const sortByToggleProps = sortable && !isSortDisabled ? column.getSortByToggleProps() : {};
                const sortId = sortBy.find((sort) => sort.id === column.id);
                return (
                  // eslint-disable-next-line react/jsx-key
                  <th
                    className={cn(
                      'border-y border-gray-200 bg-gray-100 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600',
                      isSortDisabled ? 'cursor-default' : 'cursor-pointer',
                    )}
                    {...column.getHeaderProps(sortByToggleProps)}
                    onClick={() => {
                      if (disableSortFields && disableSortFields?.includes(column.id)) return;
                      setSortBy((prev) => {
                        const isSameColumn = prev[0]?.id === column.id;
                        if (isSameColumn && prev[0]?.desc) return [];
                        const isDesc = isSameColumn && !prev[0]?.desc;
                        return [{ id: column.id, desc: isDesc }];
                      });
                      setCurrentPage(startPagination);
                    }}>
                    {column.render('Header')}
                    {sortId &&
                      ((disableSortFields && !disableSortFields?.includes(column.id)) ||
                        !disableSortFields?.length) && <span>{sortBy?.[0].desc ? '  ↓' : '  ↑'}</span>}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {visibleRows.map((row, rowIdx) => {
            prepareRow(row);
            const isLast = rowIdx === visibleRows.length - 1;
            return (
              // eslint-disable-next-line react/jsx-key
              <tr {...row.getRowProps()} className="transition-colors hover:bg-gray-50">
                {row.cells.map((cell) => (
                  // eslint-disable-next-line react/jsx-key
                  <td
                    className={cn(
                      'break-words px-6 py-4 text-sm text-gray-700',
                      !isLast && 'border-b border-gray-200',
                      cell?.column?.width,
                    )}
                    {...cell.getCellProps()}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>

        {footer && (
          <tfoot>
            {footerGroups.map((group) => (
              // Key is already present in getFooterGroupProps but eslint is not able to detect it
              // eslint-disable-next-line react/jsx-key
              <tr {...group.getFooterGroupProps()}>
                {group.headers.map((column) => (
                  // Key is already present in getFooterProps but eslint is not able to detect it
                  // eslint-disable-next-line react/jsx-key
                  <td {...column.getFooterProps()}>{column.render('Footer')}</td>
                ))}
              </tr>
            ))}
          </tfoot>
        )}
      </table>

      {pagination && (
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
          <span className="text-sm text-gray-700">
            {t('Global.Page')} {currentPage + (startPagination === 0 ? 1 : 0)} {t('Global.Of')} {pageCount}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className={cn(
                'rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold transition-colors',
                currentPage === startPagination
                  ? 'cursor-not-allowed text-gray-400'
                  : 'cursor-pointer text-gray-700 hover:bg-gray-50',
              )}
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === startPagination}>
              {t('Global.Previous')}
            </button>
            <button
              type="button"
              className={cn(
                'rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold transition-colors',
                currentPage + (startPagination === 0 ? 1 : 0) !== pageCount
                  ? 'cursor-pointer text-gray-700 hover:bg-gray-50'
                  : 'cursor-not-allowed text-gray-400',
              )}
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage + (startPagination === 0 ? 1 : 0) === pageCount}>
              {t('Global.Next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { revision } = state;
  return { revision };
};

export default connect(mapStateToProps)(TableServer);
export type { ReactTableRow };
